/**
 * Quick Reply Area (Interrupt Area)
 *
 * Unified component for handling all interrupt types:
 * - CONFIRMATION: Shows Accept/Reject buttons
 * - MULTIPLE_CHOICE: Shows option chips with optional "Select All"
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTracking } from '@context/TrackingContext';
import { EntitlementDisabledTooltip, EntitlementUsage } from '@features/entitlements';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { EventType } from '../../../types/events';
import {
  clearAllActivities,
  clearInterrupt,
  deselectAllOptions,
  selectAllOptions,
  setAssistantStatus,
  toggleOptionSelection,
} from '../store/chatSlice';
import { selectInterrupt, selectInterruptType } from '../store/selectors';

import { MultiStepWizard } from './MultiStepWizard';

interface QuickReplyAreaProps {
  onContinue: (selectedIds: string[]) => void;
  onMultiStepComplete?: (responses: Record<string, { selected_ids?: string[]; confirmed?: boolean }>) => void;
  onConfirm?: (confirmed: boolean) => void;
  /**
   * Callback when user expresses interest or dismisses EXPRESS_INTEREST interrupt
   */
  onExpressInterest?: (interested: boolean) => void;
  /**
   * Callback when interrupt is dismissed (e.g., to focus input)
   */
  onDismiss?: () => void;
  /**
   * Navigation controls for affected sections
   */
  navigation?: {
    currentIndex: number;
    totalCount: number;
    isFirst: boolean;
    isLast: boolean;
    scrollToNext: () => void;
    scrollToPrevious: () => void;
    scrollToFirst: () => void;
  };
  /**
   * Whether approval is being processed
   */
  isProcessing?: boolean;
}

export const QuickReplyArea: React.FC<QuickReplyAreaProps> = ({
  onContinue,
  onMultiStepComplete,
  onConfirm,
  onExpressInterest,
  onDismiss,
  navigation,
  isProcessing = false,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { noteId } = useParams<{ noteId: string }>();
  const { note: noteMetrics } = useEventTracking();
  const { trackEvent } = useTracking();
  const interrupt = useAppSelector(selectInterrupt);
  const interruptType = useAppSelector(selectInterruptType);
  const [truncatedOptionId, setTruncatedOptionId] = useState<string | null>(null);

  if (!interrupt?.isActive) return null;

  // Dismiss handler - handles dismissal for all interrupt types
  const handleDismiss = () => {
    if (interruptType === 'CONFIRMATION') {
      // For CONFIRMATION, send rejection to backend
      onConfirm?.(false);
    } else if (interruptType === 'EXPRESS_INTEREST') {
      // For EXPRESS_INTEREST, send not interested to backend
      onExpressInterest?.(false);
    } else if (interruptType === 'MULTI_STEP') {
      // For MULTI_STEP, clear interrupt, activities, and reset status to re-enable input
      dispatch(clearInterrupt());
      dispatch(clearAllActivities());
      dispatch(setAssistantStatus('idle'));
    } else {
      // For MULTIPLE_CHOICE, clear interrupt, activities, and reset status to re-enable input
      dispatch(clearInterrupt());
      dispatch(clearAllActivities());
      dispatch(setAssistantStatus('idle'));
    }
    // Call parent's dismiss callback (e.g., to focus input)
    onDismiss?.();
  };

  // MULTI_STEP interrupt - show wizard
  if (interruptType === 'MULTI_STEP') {
    return (
      <MultiStepWizard
        onComplete={responses => {
          // Pass the complete responses object to the handler
          onMultiStepComplete?.(responses);
        }}
        onDismiss={handleDismiss}
        isProcessing={isProcessing}
      />
    );
  }

  // CONFIRMATION interrupt - show message, navigation, and Accept/Reject buttons
  if (interruptType === 'CONFIRMATION') {
    const handleConfirm = () => onConfirm?.(true);
    const handleCancel = () => onConfirm?.(false);

    return (
      <Box
        sx={{
          position: 'relative',
          px: 2,
          pr: 5, // Extra right padding for dismiss button
          py: 1.5,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Dismiss Button */}
        <IconButton
          size="small"
          onClick={handleDismiss}
          disabled={isProcessing}
          aria-label="Dismiss"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {/* Message and Context */}
        {interrupt.message && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
            <InfoOutlinedIcon
              sx={{
                fontSize: 20,
                color: theme.palette.primary.main,
                mt: 0.25,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                {interrupt.message}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Entitlement Usage - only for train confirmations */}
        {interrupt.confirmationType === 'train' && (
          <EntitlementUsage
            capabilityName="in_note_training"
            source="note_ai_chat_train_interrupt"
            variant="compact"
            sx={{ mb: 1.5 }}
          />
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isProcessing}
            data-testid="cancel-button"
          >
            {interrupt.cancelLabel || 'Cancel'}
          </Button>
          {interrupt.confirmationType === 'train' ? (
            <EntitlementDisabledTooltip capabilityName="in_note_training">
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={isProcessing}
                data-testid="confirm-button"
              >
                {isProcessing ? 'Processing...' : interrupt.confirmLabel || 'Confirm'}
              </Button>
            </EntitlementDisabledTooltip>
          ) : (
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={isProcessing}
              data-testid="confirm-button"
            >
              {isProcessing ? 'Processing...' : interrupt.confirmLabel || 'Confirm'}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // EXPRESS_INTEREST interrupt - show "coming soon" style message for AI clinical assistant
  if (interruptType === 'EXPRESS_INTEREST') {
    const handleInterestedClick = () => {
      // Track the interest event
      // Use detectedIntent as the feature identifier, or default to 'chat_assistant'
      const feature = interrupt.detectedIntent
        ? `chat_assistant_${interrupt.detectedIntent.toLowerCase().replace(/\s+/g, '_')}`
        : 'chat_assistant';
      trackEvent(EventType.EXPRESSED_INTEREST, {
        feature,
      });
      
      // Send interested=true to backend
      onExpressInterest?.(true);
    };

    return (
      <Box
        sx={{
          position: 'relative',
          px: 2,
          pr: 5, // Extra right padding for dismiss button
          py: 2,
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Dismiss Button */}
        <IconButton
          size="small"
          onClick={handleDismiss}
          disabled={isProcessing}
          aria-label="Dismiss"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Coming Soon Message */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <InfoOutlinedIcon
            sx={{
              fontSize: 20,
              color: theme.palette.primary.main,
              mt: 0.25,
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              AI Clinical Assistant — Coming Soon
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              This chat currently lets you edit your note and train your scribe on your preferences.
              Answering clinical questions about patients isn't available yet.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Interested in being notified when this feature launches?
            </Typography>
          </Box>
        </Box>

        {/* Action Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleInterestedClick}
            disabled={isProcessing}
            data-testid="express-interest-button"
            sx={{ 
              minWidth: 150,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {isProcessing ? 'Processing...' : "I'm Interested"}
          </Button>
        </Box>
      </Box>
    );
  }

  // MULTIPLE_CHOICE interrupt - show option chips
  const options = interrupt.options || [];
  const selectedOptionIds = interrupt.selectedOptionIds || [];

  // Check if all options are selected
  const allSelected = options.length > 0 && selectedOptionIds.length === options.length;

  const handleToggle = (optionId: string, optionLabel: string) => {
    // Track suggested action click
    if (noteId) {
      noteMetrics.chatSuggestedActionClicked?.(noteId, {
        action_type: 'section_selection',
        action_id: optionId,
        action_label: optionLabel,
        note_id: noteId,
      });
    }

    dispatch(toggleOptionSelection(optionId));
  };

  const handleToggleSelectAll = () => {
    if (allSelected) {
      dispatch(deselectAllOptions());
    } else {
      dispatch(selectAllOptions());
    }
  };

  const handleContinue = () => {
    onContinue(selectedOptionIds);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        px: 2,
        pr: 5, // Extra right padding for dismiss button
        py: 1.5,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Dismiss Button */}
      <IconButton
        size="small"
        onClick={handleDismiss}
        disabled={isProcessing}
        aria-label="Dismiss"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'text.secondary',
          '&:hover': {
            color: 'text.primary',
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ display: 'block', mb: 1, fontWeight: 500 }}
      >
        {interrupt.question || 'Select options:'}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1.5,
        }}
      >
        {options.map(option => {
          const isSelected = selectedOptionIds.includes(option.id);

          return (
            <Tooltip
              key={option.id}
              title={option.label}
              open={truncatedOptionId === option.id}
              disableHoverListener
              placement="top"
            >
              <Chip
                label={option.label}
                onClick={() => handleToggle(option.id, option.label)}
                onMouseEnter={e => {
                  const label = (e.currentTarget as HTMLElement).querySelector('.MuiChip-label');
                  if (label && label.scrollWidth > label.clientWidth) {
                    setTruncatedOptionId(option.id);
                  }
                }}
                onMouseLeave={() => setTruncatedOptionId(null)}
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
                icon={isSelected ? <CheckIcon /> : undefined}
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer',
                  transition: theme.transitions.create(['all'], {
                    duration: theme.transitions.duration.short,
                  }),
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: theme.shadows[2],
                  },
                }}
                data-testid={`option-chip-${option.id}`}
              />
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="textSecondary">
            {selectedOptionIds.length > 0
              ? `${selectedOptionIds.length} selected`
              : `Select ${interrupt.minSelections ? `at least ${interrupt.minSelections}` : 'options'}`}
          </Typography>

          {/* Show Select All by default when there are 2+ options (multi-select mode) */}
          {(interrupt.allowSelectAll ?? options.length >= 2) && (
            <Button
              size="small"
              variant="text"
              onClick={handleToggleSelectAll}
              data-testid="select-all-button"
              sx={{ minWidth: 'auto', textTransform: 'none', fontSize: '0.75rem' }}
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
          )}
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={handleContinue}
          disabled={
            selectedOptionIds.length === 0 ||
            (interrupt.minSelections != null &&
              selectedOptionIds.length < interrupt.minSelections) ||
            (interrupt.maxSelections != null && selectedOptionIds.length > interrupt.maxSelections)
          }
          data-testid="continue-button"
          sx={{ minWidth: 100 }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};
